import { io } from 'socket.io-client';
import { handleWebSocketEvent } from './eventHandlers';

class WebSocketManager {
  constructor() {
    this.socket = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Increased from 5
    this.baseReconnectInterval = 1000; // Base interval for exponential backoff
    this.maxReconnectInterval = 30000; // Max 30 seconds
    this.onConnectionStatusChange = null;
    this.getCurrentTokens = null; // Callback to get current tokens from Redux
    this.connected = false;
    this.shouldReconnect = true;
    
    // Enhanced connection monitoring
    this.isPageVisible = true;
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.heartbeatFrequency = 30000; // 30 seconds
    this.heartbeatTimeoutDuration = 10000; // 10 seconds
    this.lastHeartbeat = null;
    this.connectionQuality = 'good'; // good, poor, disconnected
    this.reconnectStrategy = 'normal'; // normal, background, aggressive
    this.missedHeartbeats = 0;
    this.maxMissedHeartbeats = 3;
    
    // Page visibility handling
    this.setupPageVisibilityHandling();
    
    // Network state monitoring
    this.setupNetworkStateMonitoring();
  }

  setConnectionStatusCallback(callback) {
    this.onConnectionStatusChange = callback;
  }

  setCurrentTokensCallback(callback) {
    this.getCurrentTokens = callback;
  }

  setupPageVisibilityHandling() {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
      
      if (this.isPageVisible) {
        // Page became visible - check connection and potentially reconnect
        console.log('Page became visible, checking connection...');
        this.handlePageVisible();
      } else {
        // Page became hidden - switch to background mode
        console.log('Page became hidden, switching to background mode...');
        this.handlePageHidden();
      }
    });

    // Handle window focus/blur for additional reliability
    window.addEventListener('focus', () => {
      if (!this.isPageVisible) {
        this.isPageVisible = true;
        this.handlePageVisible();
      }
    });

    window.addEventListener('blur', () => {
      this.handlePageHidden();
    });

    // Handle before unload
    window.addEventListener('beforeunload', () => {
      this.disconnect(true);
    });
  }

  setupNetworkStateMonitoring() {
    // Monitor network connectivity
    window.addEventListener('online', () => {
      console.log('Network came online, attempting reconnection...');
      this.handleNetworkOnline();
    });

    window.addEventListener('offline', () => {
      console.log('Network went offline');
      this.handleNetworkOffline();
    });
  }

  handlePageVisible() {
    this.reconnectStrategy = 'aggressive';
    
    if (!this.connected && this.shouldReconnect) {
      // Reset reconnect attempts for aggressive reconnection
      this.reconnectAttempts = 0;
      this.updateConnectionStatus({ 
        reconnecting: true, 
        reason: 'Page became visible' 
      });
      
      // Get fresh tokens and reconnect
      const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken')
      };
      const accessToken = currentTokens.accessToken;
      const refreshToken = currentTokens.refreshToken;
      
      if (accessToken && refreshToken) {
        this.connect(accessToken, refreshToken);
      }
    } else if (this.connected) {
      // Verify connection is still alive with immediate heartbeat
      this.sendHeartbeat();
    }
  }

  handlePageHidden() {
    this.reconnectStrategy = 'background';
    // Don't disconnect, but reduce heartbeat frequency
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.startHeartbeat(60000); // Reduce to 1 minute when hidden
    }
  }

  handleNetworkOnline() {
    this.reconnectStrategy = 'aggressive';
    if (!this.connected && this.shouldReconnect) {
      this.reconnectAttempts = 0;
      const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken')
      };
      const accessToken = currentTokens.accessToken;
      const refreshToken = currentTokens.refreshToken;
      
      if (accessToken && refreshToken) {
        this.connect(accessToken, refreshToken);
      }
    }
  }

  handleNetworkOffline() {
    this.connectionQuality = 'disconnected';
    this.updateConnectionStatus({ 
      connected: false, 
      reason: 'Network offline',
      quality: 'disconnected'
    });
  }

  isConnected() {
    return this.connected;
  }

  connect(accessToken, refreshToken) {
    if (this.isConnecting || this.connected) {
      console.log('WebSocket already connected or connecting');
      return Promise.resolve();
    }

    this.disconnect();

    this.shouldReconnect = true;
    this.isConnecting = true;
    this.connectionQuality = 'connecting';
    this.updateConnectionStatus({ 
      reconnecting: false, 
      error: null, 
      quality: 'connecting',
      attempt: this.reconnectAttempts + 1
    });

    return new Promise((resolve, reject) => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const socketUrl = `${apiUrl}?access_token=${encodeURIComponent(accessToken)}&refresh_token=${encodeURIComponent(refreshToken)}`;
      
      this.socket = io(socketUrl, {
        reconnection: false,
        timeout: 15000, // Increased timeout
        transports: ['websocket'],
        forceNew: true,
        upgrade: false
      });

      this.socket.on('connect', () => {
        console.log('WebSocket connected successfully');
        this.connected = true;
        this.connectionQuality = 'good';
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.missedHeartbeats = 0;
        this.lastHeartbeat = Date.now();
        
        // Start heartbeat mechanism
        this.startHeartbeat();
        
        // Update connection status
        this.updateConnectionStatus({ 
          connected: true, 
          quality: 'good',
          reconnecting: false,
          error: null
        });
        
        // Trigger data sync if this was a reconnection
        if (this.reconnectAttempts > 0) {
          this.triggerDataSync();
        }
        
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log(`WebSocket disconnected: ${reason}`);
        this.connected = false;
        this.connectionQuality = 'disconnected';
        this.stopHeartbeat();
        
        this.updateConnectionStatus({ 
          connected: false, 
          quality: 'disconnected',
          reason: reason
        });
        
        this.handleReconnect(accessToken, refreshToken, reason);
      });

      this.socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        this.isConnecting = false;
        this.connectionQuality = 'poor';
        
        this.updateConnectionStatus({ 
          error: error.message, 
          quality: 'poor',
          attempt: this.reconnectAttempts + 1
        });
        
        this.handleReconnect(accessToken, refreshToken, 'connect_error');
        reject(error);
      });

      // Handle heartbeat responses
      this.socket.on('pong', (data) => {
        this.handleHeartbeatResponse(data);
      });

      // Handle server-initiated heartbeat
      this.socket.on('ping', () => {
        this.socket.emit('pong', { timestamp: Date.now() });
      });
      
      // Handle initial server time sync (sent on connect)
      // Used to calculate client offset for accurate countdown rendering
      this.socket.on('server_time_sync', (data) => {
        if (data.serverNowUtc) {
          this.serverTimeOffset = new Date(data.serverNowUtc).getTime() - Date.now();
          window.__serverTimeOffset = this.serverTimeOffset;
          window.__lastServerSync = Date.now();
          console.log(`Server time synced, offset: ${this.serverTimeOffset}ms`);
        }
      });

      // Handle token refresh during connection
      this.socket.on('new_access_token', (data) => {
        if (data.access_token) {
          console.log('Received new access token during WebSocket session');
          // Token will be updated in Redux via eventHandlers.js
          // No direct localStorage manipulation needed
        }
      });

      this.socket.onAny((event, data) => {
        // Don't pass pong events to general handler since they're handled directly above
        if (event !== 'pong') {
          handleWebSocketEvent(event, data);
        }
      });
    });
  }

  handleReconnect(accessToken, refreshToken, reason = 'unknown') {
    if (!this.shouldReconnect) {
      console.log('Reconnection disabled');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      this.updateConnectionStatus({ 
        reconnecting: false, 
        error: 'Maximum reconnection attempts reached. Please refresh the page.',
        quality: 'disconnected',
        maxAttemptsReached: true
      });
      return;
    }

    this.reconnectAttempts++;
    
    // Calculate exponential backoff with jitter
    const baseDelay = this.calculateReconnectDelay();
    const jitter = Math.random() * 1000; // Add up to 1 second of jitter
    const delay = baseDelay + jitter;
    
    // Adjust strategy based on page visibility and reason
    let strategy = this.reconnectStrategy;
    if (reason === 'io server disconnect' || reason === 'transport close') {
      strategy = 'aggressive'; // Server-side disconnects need immediate attention
    }
    
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${Math.round(delay/1000)}s - Strategy: ${strategy}, Reason: ${reason}`);
    
    this.updateConnectionStatus({ 
      reconnecting: true,
      attempt: this.reconnectAttempts,
      nextAttemptIn: delay,
      reason: reason,
      strategy: strategy
    });

    setTimeout(() => {
      if (!this.connected && this.shouldReconnect) {
        // Get fresh tokens in case they were updated
        const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
          accessToken: localStorage.getItem('accessToken'),
          refreshToken: localStorage.getItem('refreshToken')
        };
        const currentAccessToken = currentTokens.accessToken || accessToken;
        const currentRefreshToken = currentTokens.refreshToken || refreshToken;
        
        this.connect(currentAccessToken, currentRefreshToken);
      }
    }, delay);
  }

  calculateReconnectDelay() {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(
      this.baseReconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectInterval
    );
    
    // Reduce delay for aggressive strategy (page visible)
    if (this.reconnectStrategy === 'aggressive') {
      return Math.min(delay / 2, 5000); // Max 5 seconds when aggressive
    }
    
    // Increase delay for background strategy (page hidden)
    if (this.reconnectStrategy === 'background') {
      return Math.min(delay * 2, 60000); // Max 1 minute when in background
    }
    
    return delay;
  }

  startHeartbeat(interval = null) {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    const heartbeatInterval = interval || (this.isPageVisible ? this.heartbeatFrequency : this.heartbeatFrequency * 2);
    
    this.heartbeatInterval = setInterval(() => {
      if (this.connected && this.socket) {
        this.sendHeartbeat();
      }
    }, heartbeatInterval);
    
    console.log(`Heartbeat started with ${heartbeatInterval/1000}s interval`);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  sendHeartbeat() {
    if (!this.connected || !this.socket) {
      return;
    }

    // Get current tokens from Redux store via callback, fallback to localStorage
    const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken')
    };

    const heartbeatData = {
      timestamp: Date.now(),
      pageVisible: this.isPageVisible,
      connectionQuality: this.connectionQuality,
      // Include current tokens in heartbeat payload
      access_token: currentTokens.accessToken,
      refresh_token: currentTokens.refreshToken
    };

    // Set timeout to detect missed heartbeat response
    this.heartbeatTimeout = setTimeout(() => {
      this.handleMissedHeartbeat();
    }, this.heartbeatTimeoutDuration);

    // Send ping to server
    this.socket.emit('ping', heartbeatData);
  }

  handleHeartbeatResponse(data) {
    // Clear the timeout since we got a response
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    this.lastHeartbeat = Date.now();
    this.missedHeartbeats = 0;
    
    // Store server time for client offset calculation (for countdown rendering)
    // This allows accurate expiry countdown display without client clock dependency
    if (data.serverNowUtc) {
      this.serverTimeOffset = new Date(data.serverNowUtc).getTime() - Date.now();
      // Store globally for access by other components
      window.__serverTimeOffset = this.serverTimeOffset;
      window.__lastServerSync = Date.now();
    }
    
    // Handle token status from server
    if (data.token_status) {
      switch (data.token_status) {
        case 'expired':
          console.warn('Token expired during heartbeat - forcing reconnection');
          this.forceReconnect('Token expired');
          return;
        
        case 'refreshed':
          console.log('Token refreshed successfully during heartbeat');
          // Token was automatically refreshed, continue normally
          break;
        
        case 'invalid':
          console.error('Invalid token during heartbeat - forcing reconnection');
          this.forceReconnect('Invalid token');
          return;
        
        case 'error':
          console.error('Server error during heartbeat processing');
          this.connectionQuality = 'poor';
          break;
        
        case 'valid':
        default:
          // Token is valid, continue normally
          break;
      }
    }
    
    // Update connection quality based on response time
    const responseTime = this.lastHeartbeat - data.timestamp;
    if (responseTime < 1000) {
      this.connectionQuality = 'good';
    } else if (responseTime < 3000) {
      this.connectionQuality = 'fair';
    } else {
      this.connectionQuality = 'poor';
    }

    this.updateConnectionStatus({
      quality: this.connectionQuality,
      lastHeartbeat: this.lastHeartbeat,
      responseTime: responseTime,
      tokenStatus: data.token_status || 'unknown',
      serverTimeOffset: this.serverTimeOffset
    });

    // Log any messages from server
    if (data.message) {
      console.log(`Heartbeat message: ${data.message}`);
    }
  }

  handleMissedHeartbeat() {
    this.missedHeartbeats++;
    console.warn(`Missed heartbeat ${this.missedHeartbeats}/${this.maxMissedHeartbeats}`);
    
    this.connectionQuality = 'poor';
    this.updateConnectionStatus({
      quality: 'poor',
      missedHeartbeats: this.missedHeartbeats
    });

    if (this.missedHeartbeats >= this.maxMissedHeartbeats) {
      console.error('Too many missed heartbeats, forcing reconnection');
      this.forceReconnect('Heartbeat timeout');
    }
  }

  forceReconnect(reason = 'Manual reconnection') {
    console.log(`Forcing reconnection: ${reason}`);
    
    // Disconnect current connection
    if (this.socket) {
      this.socket.disconnect();
    }
    
    this.connected = false;
    this.stopHeartbeat();
    
    // Reset connection state
    this.connectionQuality = 'disconnected';
    this.updateConnectionStatus({
      connected: false,
      quality: 'disconnected',
      reason: reason
    });

    // Get tokens and reconnect
    const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken')
    };
    const accessToken = currentTokens.accessToken;
    const refreshToken = currentTokens.refreshToken;
    
    if (accessToken && refreshToken) {
      this.handleReconnect(accessToken, refreshToken, reason);
    }
  }

  triggerDataSync() {
    // Emit a data sync request to get any missed updates
    if (this.connected && this.socket) {
      console.log('Triggering data synchronization after reconnection');
      
      // Get lastSyncTimestamp from Redux store if available, fallback to localStorage
      const currentState = this.getCurrentTokens ? this.getCurrentTokens() : {};
      const lastSyncTime = currentState.lastSyncTimestamp || localStorage.getItem('lastSyncTimestamp') || Date.now() - 300000; // Default to 5 minutes ago
      
      this.socket.emit('request_sync', {
        lastSyncTimestamp: lastSyncTime,
        pageVisible: this.isPageVisible
      });
    }
  }

  // Manual reconnection method for user-initiated reconnects
  manualReconnect() {
    console.log('Manual reconnection requested');
    this.reconnectAttempts = 0; // Reset attempts for manual reconnect
    this.reconnectStrategy = 'aggressive';
    
    const currentTokens = this.getCurrentTokens ? this.getCurrentTokens() : {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken')
    };
    const accessToken = currentTokens.accessToken;
    const refreshToken = currentTokens.refreshToken;
    
    if (accessToken && refreshToken) {
      if (this.connected) {
        this.disconnect();
      }
      return this.connect(accessToken, refreshToken);
    } else {
      throw new Error('No authentication tokens available');
    }
  }

  // Get connection health information
  getConnectionHealth() {
    return {
      connected: this.connected,
      quality: this.connectionQuality,
      lastHeartbeat: this.lastHeartbeat,
      missedHeartbeats: this.missedHeartbeats,
      reconnectAttempts: this.reconnectAttempts,
      isPageVisible: this.isPageVisible,
      strategy: this.reconnectStrategy
    };
  }

  disconnect(preventReconnect = false) {
    if (preventReconnect) {
      this.shouldReconnect = false;
    }
    
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.connected = false;
    this.connectionQuality = 'disconnected';
    this.reconnectAttempts = 0;
    this.missedHeartbeats = 0;
    
    this.updateConnectionStatus({ 
      connected: false, 
      reconnecting: false, 
      error: null,
      quality: 'disconnected'
    });
  }

  updateConnectionStatus(status) {
    const fullStatus = {
      ...status,
      timestamp: Date.now(),
      health: this.getConnectionHealth()
    };
    
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(fullStatus);
    }
  }
}

export default new WebSocketManager();
